import React, { useMemo, useState } from 'react'; // Added useState
import ContainerHeader from '../components/ContainerHeader';
import { ColumnDirective, ColumnsDirective, Filter, GridComponent, Group } from '@syncfusion/ej2-react-grids';
import { Inject, Page, Sort, Search, Toolbar } from '@syncfusion/ej2-react-grids';
import { Button } from '@material-ui/core';
import { Box, MenuItem, Tab, Tabs, TextField } from '@mui/material';
import ConfirmationDialog from '../components/ConfirmDialogs/ConfirmationDialog';
import { dateTemplate } from '../GenTask/components/SharedFormFields';
import useRequisition from './hooks/useRequisition';

// Import the Add/Edit form component
import AddPurchaseRequisitionForm from './components/AddRequistion';

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

class DebugBoundary extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentCatch(error, info) {
    console.error('ErrorBoundary caught error:', error, info);
  }
  render() {
    if (this.state.hasError) return <div>Error in grid rendering</div>;
    return this.props.children;
  }
}

const PurchaseRequisition = props => {
  const pageSettings = useMemo(
    () => ({
      pageSize: 30,
      pageSizes: [100, 200],
    }),
    [],
  );
  const filterSettings = {
    type: 'Excel',
  };

  // 1. Color filter state engine
  const [selectedColorFilter, setSelectedColorFilter] = useState('ALL');

  const {
    currentTabPurchaseReqData,
    currentTab,
    handleTabChange,
    openModal,
    setOpenModal,
    isEdit,
    selectedReq,
    handleAddNew,
    handleOpenEdit,
    handleSubmit,
    handleEdit,
    uploadedDocs,
    handleFileDelete,
    viewFile,
    openConfirmationDialog,
    setOpenConfirmationDialog,
    confirmationDialogProps,
    handleOpenConfirmationDialog,
    spList,
    supplierList,
    departmentList,
  } = useRequisition();

  // Reset local color filter if the main tab changes to avoid state confusion
  const onTabChangeWrapper = (event, newValue) => {
    setSelectedColorFilter('ALL');
    handleTabChange(event, newValue);
  };

  // 2. Client-side dataset extraction matching rowDataBound styling rules
  const filteredGridData = useMemo(() => {
    if (!currentTabPurchaseReqData) return [];
    if (selectedColorFilter === 'ALL') return currentTabPurchaseReqData;

    return currentTabPurchaseReqData.filter(item => {
      const isDieselPurchase = item['purchaseOrReturn'] === 'Diesel Purchase';
      const status = item['requisitionStatus'];

      // Extract theoretical target color assignment string
      let rowColorKey = 'ORANGE'; // Fallback / default orange catch
      if (isDieselPurchase && status === 'Requested') {
        rowColorKey = 'RED';
      } else if (status === 'Ordered') {
        rowColorKey = 'GREY';
      } else if (status === 'Approved' || status === 'Cancelled') {
        rowColorKey = 'GREEN';
      }

      return rowColorKey === selectedColorFilter;
    });
  }, [currentTabPurchaseReqData, selectedColorFilter]);

  const toolbarOptions = [
    { text: 'Search', tooltipText: 'Search', prefixIcon: 'e-search', id: 'search' },
    {
      template: () => (
        <div style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            style={{ marginLeft: '10px' }}
            onClick={() => handleAddNew(null)}>
            + Add Purchase Requisition
          </Button>
        </div>
      ),
      id: 'addNewWoodChipSample',
      align: 'Left',
    },
  ];

  function editLinkTemplate(props) {
    return (
      <Button
        key={`${props.id}_site`}
        style={{ color: 'blue', fontWeight: 'bold', width: '100%' }}
        onClick={() => handleOpenEdit(props)}>
        {props.id}
      </Button>
    );
  }

  const rowDataBound = args => {
    const isDieselPurchase = args.data['purchaseOrReturn'] === 'Diesel Purchase';
    const isReturn = args.data['purchaseOrReturn'] === 'Return';
    const status = args.data['requisitionStatus'];

    if (status === 'Cancelled') {
      args.row.style.backgroundColor = '#6ccd74';
      args.row.style.textDecoration = 'line-through';
    } else if (isDieselPurchase && status === 'Requested') {
      args.row.style.backgroundColor = '#fd6868';
    } else if (status === 'Ordered') {
      args.row.style.backgroundColor = '#dadada';
    } else if (status === 'Approved') {
      args.row.style.backgroundColor = '#6ccd74';
    } else {
      args.row.style.backgroundColor = '#ffd37c';
    }

    console.log(isReturn, 'isReturn')

    // Apply bold red text on top of whatever background color was set above
    if (isReturn) {
    const cells = args.row.querySelectorAll('.e-rowcell');
    cells.forEach(cell => {
      cell.style.fontWeight = 'bold';
      cell.style.color = 'red';
    });
  }
  };

  return (
    <div className="app-wrapper siteWrapper">
      <ContainerHeader match={props.match} title={'Purchase Requisition Form'} />

      {/* Container aligned to display tabs on left and functional filter controls on right */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          pr: 2,
        }}>
        <Tabs value={currentTab} onChange={onTabChangeWrapper} aria-label="basic tabs example">
          <Tab color="primary" label="All" {...a11yProps(0)} />
          <Tab color="primary" label="Purchase" {...a11yProps(1)} />
          <Tab color="primary" label="Diesel Purchase" {...a11yProps(2)} />
          <Tab color="primary" label="Recharge" {...a11yProps(3)} />
        </Tabs>

        {/* Dynamic Color filter dropdown layout */}
        <TextField
          select
          size="small"
          label="Color Status"
          value={selectedColorFilter}
          onChange={e => setSelectedColorFilter(e.target.value)}
          sx={{
            minWidth: 200,
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: '14px',
            },
          }}>
          <MenuItem value="ALL" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', border: '1px solid #000000', bgcolor: 'transparent' }} />
            All
          </MenuItem>

          <MenuItem value="RED" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#fd6868' }} />
            Red (Diesel Purchase Requested)
          </MenuItem>

          <MenuItem value="ORANGE" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ffd37c' }} />
            Orange (Requested or Delivered)
          </MenuItem>

          <MenuItem value="GREY" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#dadada', border: '1px solid #e0e0e0' }} />
            Grey (Ordered)
          </MenuItem>

          <MenuItem value="GREEN" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#6ccd74', border: '1px solid #c8e6c9' }} />
            Green (Approved or Cancelled)
          </MenuItem>


        </TextField>
      </Box>

      <div style={{ height: 'calc(100vh - 195px)', width: '100%', paddingTop: '0.5rem' }}>
        <DebugBoundary>
          <style>{`
    .e-grid .e-row:hover .e-rowcell,
    .e-grid .e-row:hover.e-altrow .e-rowcell {
      background-color: inherit !important;
    }
  `}</style>
          <GridComponent
            rowDataBound={rowDataBound}
            dataSource={filteredGridData}
            filterSettings={filterSettings}
            allowPaging={true}
            pageSettings={pageSettings}
            allowSorting={true}
            allowTextWrap={true}
            allowResizing={true}
            allowMultiSorting={true}
            allowReordering={true}
            allowFiltering={true}
            style={{ minHeight: 'auto !important' }}
            height="100%"
            toolbar={toolbarOptions}>
            <ColumnsDirective>
              <ColumnDirective
                field="id"
                headerText="PO Req Number"
                width="90"
                textAlign="Left"
                isPrimaryKey={true}
                template={editLinkTemplate}
              />
              <ColumnDirective field="purchaseOrReturn" headerText="Purchase Or Return" width="100" />
              <ColumnDirective field="requisitionStatus" headerText="Status" width="100" />
              <ColumnDirective field="submissionDate" headerText="Date" width="100" formatter={dateTemplate} />
              <ColumnDirective field="pO_ReqNumberDisplay" headerText="PO Req Display" width="100" />
              {/* <ColumnDirective field="pO_ReqNumberDisplay" headerText="PO Req Number" width="100" /> */}
              <ColumnDirective field="originatedDisplayName" headerText="Originated By" width="100" />
              <ColumnDirective field="spName" headerText="WH Number" width="100" />
              <ColumnDirective field="litresOrdered" headerText="Litres Ordered" width="80" textAlign="Right" />
              <ColumnDirective field="litresDelivered" headerText="Litres Delivered" width="80" textAlign="Right" />
              <ColumnDirective field="totalNetValue" headerText="Total Net Value" width="80" textAlign="Right" />
              <ColumnDirective field="emailToSupplier" headerText="Email To Supplier" width="120" />
              <ColumnDirective field="departmentMachine" headerText="Department Machine" width="100" />
              <ColumnDirective field="supplierName" headerText="Supplier Name" width="100" />
              <ColumnDirective field="description" headerText="Description" width="100" />
            </ColumnsDirective>
            <Inject services={[Page, Sort, Filter, Group, Search, Toolbar]} />
          </GridComponent>
        </DebugBoundary>
      </div>

      {/* Hooked Up Add / Edit Form Modal Dialog Component */}
      <AddPurchaseRequisitionForm
        open={openModal}
        setOpen={setOpenModal}
        isEdit={isEdit}
        selectedPurchaseReq={selectedReq}
        handlePurchseReqSubmit={handleSubmit}
        handleRfqEdit={handleEdit}
        handleOpenConfirmationDialog={handleOpenConfirmationDialog}
        spList={spList}
        uploadedDocs={uploadedDocs}
        handleFileDelete={handleFileDelete}
        viewFile={viewFile}
        supplierList={supplierList}
        departmentList={departmentList}
      />

      <ConfirmationDialog
        open={openConfirmationDialog}
        setOpen={setOpenConfirmationDialog}
        confirmationFun={confirmationDialogProps.confirmFnc}
        declineFunction={confirmationDialogProps.cancleFnc}
        confirmationText={confirmationDialogProps.confirmText}
      />
    </div>
  );
};

export default PurchaseRequisition;
